import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const plan = sp.get("plan");
  const paid = plan === "pro" || plan === "proplus";
  const planLabel = plan === "proplus" ? "Pro+" : "Pro";
  const [tab, setTab] = useState(sp.get("tab") === "signup" ? "signup" : "login");
  const [accountType, setAccountType] = useState(sp.get("type") === "foodie" ? "foodie" : "owner");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const dest = (role) => (role === "admin" ? "/admin" : role === "foodie" ? "/favoris" : "/dashboard");
  useEffect(() => { if (user) nav(dest(user.role)); }, [user, nav]);
  useEffect(() => { if (paid) setAccountType("owner"); }, [paid]);

  const goCheckout = async (u) => {
    if (paid && u.role === "owner") {
      try {
        const { data } = await api.post("/payments/checkout", { lookup_key: `${plan}_monthly`, origin_url: window.location.origin, user_id: u.id });
        window.location.href = data.checkout_url; return true;
      } catch (err) { toast.error(apiError(err)); }
    }
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = tab === "signup"
        ? await register(form.name, form.email, form.password, accountType)
        : await login(form.email, form.password);
      if (await goCheckout(u)) return;
      toast.success(tab === "signup" ? "Bienvenue sur StreatEat !" : "Connecté !");
      nav(dest(u.role));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  let submitLabel;
  if (busy) submitLabel = "Un instant...";
  else if (paid) submitLabel = tab === "signup" ? `Créer mon compte et passer en ${planLabel}` : `Se connecter et passer en ${planLabel}`;
  else if (tab === "signup") submitLabel = accountType === "foodie" ? "Créer mon compte gourmand" : "Créer ma page gratuitement";
  else submitLabel = "Se connecter";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="text-center mb-6"><Logo className="justify-center" /></div>
      <div className="bg-white rounded-3xl shadow-warm-lg p-7">
        <div className="flex bg-cream-bg rounded-full p-1 mb-6">
          <button onClick={() => setTab("signup")} className={`flex-1 py-2 rounded-full font-bold text-sm transition-colors ${tab === "signup" ? "bg-street-gold text-white" : "text-urban-teal"}`} data-testid="tab-signup">Inscription</button>
          <button onClick={() => setTab("login")} className={`flex-1 py-2 rounded-full font-bold text-sm transition-colors ${tab === "login" ? "bg-urban-teal text-white" : "text-urban-teal"}`} data-testid="tab-login">Connexion</button>
        </div>
        {paid && (
          <div className="bg-street-gold/10 border border-street-gold/30 rounded-xl p-3 mb-4 text-sm text-urban-teal" data-testid="paid-plan-banner">
            <b>Offre {planLabel} sélectionnée.</b> {tab === "signup" ? "Créez votre compte, puis vous serez redirigé vers le paiement sécurisé." : "Connectez-vous pour finaliser votre abonnement."}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          {tab === "signup" && (
            <>
              {!paid && (
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setAccountType("owner")} className={`rounded-xl p-3 border-2 text-left transition-colors ${accountType === "owner" ? "border-street-gold bg-street-gold/5" : "border-cream-bg"}`} data-testid="account-owner">
                  <span className="block font-bold text-sm text-urban-teal">Je gère un foodtruck</span>
                  <span className="block text-xs text-urban-teal/50">Référencer mon activité</span>
                </button>
                <button type="button" onClick={() => setAccountType("foodie")} className={`rounded-xl p-3 border-2 text-left transition-colors ${accountType === "foodie" ? "border-fresh-mint bg-fresh-mint/5" : "border-cream-bg"}`} data-testid="account-foodie">
                  <span className="block font-bold text-sm text-urban-teal">Je suis gourmand</span>
                  <span className="block text-xs text-urban-teal/50">Favoris & alertes</span>
                </button>
              </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase text-urban-teal/50">Votre nom</label>
                <input value={form.name} onChange={set("name")} required placeholder="Ex. Pauline" className="w-full mt-1 border-2 border-cream-bg rounded-xl px-3 py-2.5 outline-none focus:border-urban-teal" data-testid="auth-name" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-urban-teal/50">Email</label>
            <input type="email" value={form.email} onChange={set("email")} required placeholder="vous@foodtruck.fr" className="w-full mt-1 border-2 border-cream-bg rounded-xl px-3 py-2.5 outline-none focus:border-urban-teal" data-testid="auth-email" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-urban-teal/50">Mot de passe</label>
            <input type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="w-full mt-1 border-2 border-cream-bg rounded-xl px-3 py-2.5 outline-none focus:border-urban-teal" data-testid="auth-password" />
          </div>
          <Button type="submit" disabled={busy} className={`w-full rounded-full h-12 font-bold text-white ${tab === "signup" ? "bg-street-gold hover:bg-street-gold/90" : "bg-urban-teal hover:bg-urban-teal/90"}`} data-testid="auth-submit">
            {submitLabel}
          </Button>
        </form>
        {tab === "login" && (
          <p className="text-center mt-3"><button type="button" onClick={() => nav("/mot-de-passe-oublie")} className="text-sm text-street-gold font-semibold hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</button></p>
        )}
        {!paid && <p className="text-xs text-urban-teal/40 mt-4 text-center">Gratuit · sans carte bancaire · sans engagement.</p>}
      </div>
    </div>
  );
}
